import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode
  hint?: ReactNode
  indeterminate?: boolean
  wrapperClassName?: string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { label, hint, indeterminate, className, wrapperClassName, id, disabled, checked, ...props },
    ref,
  ) => {
    const autoId = useId()
    const fieldId = id ?? autoId
    return (
      <label
        htmlFor={fieldId}
        className={cn(
          'inline-flex items-start gap-2 cursor-pointer select-none',
          disabled && 'cursor-not-allowed opacity-60',
          wrapperClassName,
        )}
      >
        <span className="relative inline-flex shrink-0 mt-0.5">
          <input
            ref={(el) => {
              if (typeof ref === 'function') ref(el)
              else if (ref) ref.current = el
              if (el) el.indeterminate = Boolean(indeterminate)
            }}
            id={fieldId}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            className={cn(
              'peer appearance-none h-4 w-4 rounded border border-gray-300 bg-white transition',
              'checked:bg-brand-700 checked:border-brand-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
              indeterminate && 'bg-brand-700 border-brand-700',
              className,
            )}
            {...props}
          />
          <span
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center text-white',
              indeterminate ? 'opacity-100' : 'opacity-0 peer-checked:opacity-100',
            )}
          >
            {indeterminate ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" />}
          </span>
        </span>
        {(label || hint) && (
          <span className="flex flex-col">
            {label ? <span className="text-body text-gray-900">{label}</span> : null}
            {hint ? <span className="text-caption text-gray-500">{hint}</span> : null}
          </span>
        )}
      </label>
    )
  },
)
Checkbox.displayName = 'Checkbox'
